import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Filter, Loader, PlusIcon, SendIcon, UserIcon, UsersRoundIcon } from "lucide-react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { ChatType, fetchChatsPage, useChatsPaginated, ChatStream, ChatsParams, ChatTypeOptions, ChatSourceOptions, MessageGroupOptions, useSendTestMessage, TwilioMessageName, SendTestMessageValidation } from "@/lib/api/chats";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatList } from "./ChatList";
import { ChatDetail } from "./ChatDetail";
import { CreateChatStream } from "./ManageChatStream";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { BearInput, BearSelectInput } from "@/lib/form/ui";
import { useQueryParam } from "@/hooks/use-query-param";
import { findByKey } from "@/lib/prelude";
import { GroupChatSection } from "./GroupChatSection";
import { useListCustomers } from "@/lib/api/customer";
import { Customer, CustomerSourcePhoneNumber } from "@/lib/api/reports";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type FormType = "manual" | "customer";

export function TestMessageModal() {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formType, setFormType] = useState<FormType>("manual");
  const sendTestMessage = useSendTestMessage();
  const customersResult = useListCustomers({ fetchInBackground: true });

  const [formData, setFormData] = useState({
    sms_number: "",
    message_service_env: TwilioMessageName.DestinationMessagingSid,
    message: ""
  });

  const [customerFormData, setCustomerFormData] = useState({
    customer_id: "",
    phone_number_selection: "",
    message_service_env: TwilioMessageName.DestinationMessagingSid,
    message: ""
  });

  const customers: Customer[] = customersResult.data?.data || [];
  const selectedCustomer = customers.find(c => c.id === customerFormData.customer_id);
  const availablePhoneNumbers = (selectedCustomer?.source_phone_numbers || []);
  if (selectedCustomer?.phone_number && !availablePhoneNumbers.some((wrapper) => wrapper.phone_number === selectedCustomer.phone_number)) {
    availablePhoneNumbers.push({ phone_number: selectedCustomer.phone_number, label: '', source_type: '' });
  }

  const onClose = () => {
    setIsModalOpen(false);
    // Clear all form data when closing
    setFormData({
      sms_number: "",
      message_service_env: TwilioMessageName.DestinationMessagingSid,
      message: ""
    });
    setCustomerFormData({
      customer_id: "",
      phone_number_selection: "",
      message_service_env: TwilioMessageName.DestinationMessagingSid,
      message: ""
    });
  };

  const onStatusChange = (status: boolean) => {
    setIsModalOpen(status);
    if (!status) { onClose(); }
  };

  const handleFormTypeChange = (newFormType: FormType) => {
    setFormType(newFormType);
    // Clear form data when switching form types
    setFormData({
      sms_number: "",
      message_service_env: TwilioMessageName.DestinationMessagingSid,
      message: ""
    });
    setCustomerFormData({
      customer_id: "",
      phone_number_selection: "",
      message_service_env: TwilioMessageName.DestinationMessagingSid,
      message: ""
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomerInputChange = (field: string, value: string) => {
    setCustomerFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset phone number selection when customer changes
    if (field === "customer_id") {
      const selectedCustomer = customers.find(c => c.id === value);
      const availablePhoneNumbers = (selectedCustomer?.source_phone_numbers || []);
      if (selectedCustomer?.phone_number && !availablePhoneNumbers.some((wrapper) => wrapper.phone_number === selectedCustomer.phone_number)) {
        availablePhoneNumbers.push({ phone_number: selectedCustomer.phone_number, label: '', source_type: '' });
      }

      setCustomerFormData(prev => ({
        ...prev,
        phone_number_selection: availablePhoneNumbers?.[0]?.phone_number || ""
      }));
    }
  };

  const handleSendTestMessage = async () => {
    if (isSending) return;
    setIsSending(true);

    try {
      let validationData;

      if (formType === "manual") {
        if (!formData.sms_number || (formData.sms_number && !parsePhoneNumber(formData.sms_number, "US")?.isValid())) {
          toast({
            title: "Invalid Number",
            description: "",
          });
          return;
        }

        validationData = {
          ...formData,
          sms_number: parsePhoneNumber(formData.sms_number, "US")?.format("E.164") || formData.sms_number
        };
      } else {
        // Customer form type
        const selectedPhoneNumber = availablePhoneNumbers.find(p => p.phone_number === customerFormData.phone_number_selection);
        if (!selectedPhoneNumber) {
          toast({
            title: "Missing phone number",
            description: "Please select a phone number",
          });
          return;
        }

        validationData = {
          customer_id: customerFormData.customer_id,
          sms_number: selectedPhoneNumber.phone_number,
          message_service_env: customerFormData.message_service_env,
          message: customerFormData.message
        };
      }

      const parsedDataValidation = SendTestMessageValidation.safeParse(validationData);

      if (!parsedDataValidation.success) {
        toast({
          title: "Missing info",
          description: "SMS number and message are required",
        });
        return;
      }

      const result = await sendTestMessage(parsedDataValidation.data!);
      if (result.success) {
        toast({
          title: "Sent",
          description: "",
        });
      } else {
        toast({
          title: "Failed to send message",
          description: "",
        });
      }
    } catch (error) {
      console.error("Failed to send test message", error);
      toast({
        title: "Failed to send message",
        description: "",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={onStatusChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 mr-2">
          <SendIcon className="h-4 w-4" />
          New
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] md:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send New Message</DialogTitle>
        </DialogHeader>

        <Tabs value={formType} onValueChange={(value) => handleFormTypeChange(value as FormType)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            <TabsTrigger value="customer">Customer Selection</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4">
            <form className="grid gap-4 py-4" onSubmit={handleSendTestMessage}>
              <BearSelectInput
                disabled={isSending}
                key={"message_service_env"}
                required
                name={"message_service_env"}
                label="Feature"
                value={formData.message_service_env}
                onValueChange={(value) => handleInputChange("message_service_env", value?.toLocaleUpperCase())}
                options={MessageGroupOptions}
              />
              <div className="grid grid-cols-1  items-center gap-4">
                <BearInput
                  disabled={isSending}
                  key={"chat_test_message_sms_number"}
                  required
                  name={"sms_number"}
                  label={"To Number"}
                  value={(() => {
                    const phone_number = formData.sms_number;
                    if (!phone_number?.trim()) return phone_number || "";
                    return new PhoneNumberFormatterAsYouType('US').input(phone_number);
                  })()}
                  placeholder={""}
                  onChange={(event) => handleInputChange("sms_number", event.target.value)}
                />
              </div>
              <div className="grid grid-cols-1  items-center gap-4">
                <BearInput
                  disabled={isSending}
                  key={"chat_test_message"}
                  required
                  name={"message"}
                  label={"Message"}
                  value={formData.message}
                  placeholder={""}
                  onChange={(event) => handleInputChange("message", event.target.value)}
                />
              </div>
            </form>
          </TabsContent>

          <TabsContent value="customer" className="space-y-4">
            <form className="grid gap-4 py-4" onSubmit={handleSendTestMessage}>
              <BearSelectInput
                disabled={isSending}
                key={"customer_message_service_env"}
                required
                name={"message_service_env"}
                label="Feature"
                value={customerFormData.message_service_env}
                onValueChange={(value) => handleCustomerInputChange("message_service_env", value?.toLocaleUpperCase())}
                options={MessageGroupOptions}
              />
              <div className="grid grid-cols-1 items-center gap-4">
                <label className="text-sm font-medium">Customer</label>
                <Combobox
                  className="w-full"
                  popoverClassName="min-w-[400px]"
                  disabled={isSending}
                  isModal
                  dwimOnChange
                  virtualizeList
                  value={customerFormData.customer_id}
                  onValueChange={(value) => handleCustomerInputChange("customer_id", value || "")}
                  options={customers.map(customer => ({
                    value: customer.id,
                    label: customer.long_name || customer.short_name
                  }))}
                  placeholder="Select customer..."
                />
              </div>
              {selectedCustomer && availablePhoneNumbers.length > 0 && (
                <div className="grid grid-cols-1 items-center gap-4">
                  <label className="text-sm font-medium">Phone Number</label>
                  <Combobox
                    className="w-full"
                    popoverClassName="min-w-[400px]"
                    disabled={isSending}
                    isModal
                    dwimOnChange
                    value={customerFormData.phone_number_selection}
                    onValueChange={(value) => handleCustomerInputChange("phone_number_selection", value || "")}
                    options={availablePhoneNumbers.map(phoneNumber => ({
                      value: phoneNumber.phone_number,
                      label: [phoneNumber.label, phoneNumber.phone_number].filter(Boolean).join(" -  ")
                    }))}
                    placeholder="Select phone number..."
                  />
                </div>
              )}
              {selectedCustomer && !availablePhoneNumbers.length && (
                <span className="text-red-500 font-bold">Found no phone numbers</span>
              )}
              <div className="grid grid-cols-1 items-center gap-4">
                <BearInput
                  disabled={isSending}
                  key={"customer_chat_test_message"}
                  required
                  name={"message"}
                  label={"Message"}
                  value={customerFormData.message}
                  placeholder={""}
                  onChange={(event) => handleCustomerInputChange("message", event.target.value)}
                />
              </div>
            </form>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleSendTestMessage}
            disabled={
              isSending ||
              (formType === "manual"
                ? !formData.message || !formData.sms_number || !formData.message_service_env
                : !customerFormData.message || !customerFormData.customer_id || !customerFormData.phone_number_selection || !customerFormData.message_service_env
              )
            }
          >
            {isSending ? <Loader className="w-4 h-4 animate-spin" /> : <span>Send</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
